import { toast } from 'sonner'

/**
 * Tells the user a dictionary change didn't save. With the browser offline that is the Figma
 * "Dictionary Failure Notifications / Network Failure" toast; otherwise `showFailure` shows the
 * change's own failure toast.
 */
export function notifyDictionaryChangeFailed(showFailure: () => void) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    toast.error('No internet connection.', {
      description: "Dictionary changes won't be saved until you're back online.",
    })

    return
  }

  showFailure()
}

interface PopoverSaveFailure {
  /** Why the save didn't go through: the backend was busy, the session ended, or anything else. */
  reason: 'failed' | 'busy' | 'sessionExpired'
  /** The word that was being added. */
  word: string
  /** A Change & Add, which also leaves the text as it was. */
  changesText: boolean
}

/**
 * A fix-popover save to the dictionary that didn't go through (SC-16): Figma "Dictionary Failure
 * Notifications" Popover Save Failure and Change and Add Failure, which name the word, and Busy and
 * Session Expired (draft wording in Figma). With the browser offline it is the network toast.
 */
export function notifyPopoverSaveFailed({ reason, word, changesText }: PopoverSaveFailure) {
  if (reason === 'busy') {
    toast.error('Busy right now.', { description: 'Try again in a moment.' })

    return
  }

  if (reason === 'sessionExpired') {
    toast.error('Your session expired.', { description: 'Sign in again to save changes.' })

    return
  }

  notifyDictionaryChangeFailed(() =>
    toast.error(`Couldn't save “${word}”.`, {
      description: changesText
        ? "Your text wasn't changed and the word wasn't added. Try again."
        : "The word wasn't added to the dictionary. Try again.",
    })
  )
}

/**
 * A remove or a Clear All that didn't go through: Figma "Dictionary Failure Notifications /
 * Dictionary Update Failure", or the network toast when offline.
 */
export function notifyDictionaryUpdateFailed() {
  notifyDictionaryChangeFailed(() =>
    toast.error("Couldn't update the dictionary.", {
      description: "Your custom words weren't changed. Try again.",
    })
  )
}
